import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adConfigApi, type AdConfigPlatform } from '../api/adConfigApi';
import { adConfigKeys } from '../queryKeys';

/**
 * `pollMs` turns on background refetching while the caller is waiting for a
 * queued sync to land. The sync endpoint returns as soon as the job is queued,
 * so without polling the only thing that ever refreshes this query is React
 * Query's window-focus refetch — which is why a completed sync used to show up
 * minutes later, or not at all.
 */
export function useAdConfigOverview(
  practiceId: string | undefined,
  locationId: string | undefined,
  pollMs: number | false = false,
) {
  return useQuery({
    queryKey: adConfigKeys.overview(practiceId ?? '', locationId ?? ''),
    queryFn: () => adConfigApi.getOverview(practiceId!, locationId!),
    enabled: Boolean(practiceId && locationId),
    staleTime: 60_000,
    refetchInterval: pollMs,
  });
}

/**
 * Exporting hits the ad platforms live, so it is a mutation rather than a
 * query: it should only ever run when an operator asks for it, and it must not
 * be re-fired by a refetch.
 */
export function useAdConfigExport(practiceId: string | undefined, locationId: string | undefined) {
  return useMutation({
    mutationFn: () => adConfigApi.exportFile(practiceId!, locationId!),
  });
}

/**
 * Queues a sync. Resolving means the job was accepted, not that it ran — a
 * worker does the work afterwards, so the caller has to watch the overview for
 * the result rather than invalidate here. Invalidating on success would refetch
 * data the worker has not touched yet.
 */
export function useAdConfigSync(practiceId: string | undefined, locationId: string | undefined) {
  return useMutation({
    mutationFn: (platform: AdConfigPlatform) =>
      adConfigApi.sync(practiceId!, locationId!, platform),
  });
}

/** "Re-sync from Google". Refetches the overview once it lands, since settings and budgets moved. */
export function useAdConfigResyncGoogle(practiceId: string | undefined, locationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => adConfigApi.resyncGoogle(practiceId!, locationId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adConfigKeys.overview(practiceId ?? '', locationId ?? ''),
      });
    },
  });
}

/** Refetch the overview now — used after an export, which records what it read. */
export function useRefreshAdConfigOverview(
  practiceId: string | undefined,
  locationId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: adConfigKeys.overview(practiceId ?? '', locationId ?? ''),
    });
  }, [queryClient, practiceId, locationId]);
}

export function useAdConfigDiff(practiceId: string | undefined, locationId: string | undefined) {
  return useMutation({
    mutationFn: (markdown: string) => adConfigApi.diff(practiceId!, locationId!, markdown),
  });
}

/**
 * Parse an authored file into a plan of new campaigns to create. Writes
 * nothing; it is a mutation only because it runs on demand from a file the
 * operator picked, never on mount.
 */
export function useAdConfigCreatePlan(
  practiceId: string | undefined,
  locationId: string | undefined,
) {
  return useMutation({
    mutationFn: (markdown: string) => adConfigApi.createPlan(practiceId!, locationId!, markdown),
  });
}

/**
 * Create PracticeNova campaign drafts from an authored file. This writes
 * campaign records (the ready campaigns become drafts in the Campaigns view),
 * but nothing on Google or Meta.
 */
export function useCreateAdConfigDrafts(
  practiceId: string | undefined,
  locationId: string | undefined,
) {
  return useMutation({
    mutationFn: (input: { markdown: string; existingStrategy: 'skip' | 'update' }) =>
      adConfigApi.createDrafts(practiceId!, locationId!, input.markdown, input.existingStrategy),
  });
}

/**
 * Replace PracticeNova's copy of the campaigns an exported file describes. The
 * change shows on each campaign's Setup tab; nothing is written to Google.
 */
export function useReplaceFromFile(practiceId: string | undefined, locationId: string | undefined) {
  return useMutation({
    mutationFn: (input: { markdown: string; filename?: string; externalIds?: string[] }) =>
      adConfigApi.replaceFromFile(
        practiceId!,
        locationId!,
        input.markdown,
        input.filename,
        input.externalIds,
      ),
  });
}

/**
 * Stage a re-imported file's changes as pending revisions on the matching live
 * campaigns, for review on their Setup tab. Writes nothing to Google or Meta.
 */
export function useStageLiveUpdates(
  practiceId: string | undefined,
  locationId: string | undefined,
) {
  return useMutation({
    mutationFn: (input: { markdown: string }) =>
      adConfigApi.stageLiveUpdates(practiceId!, locationId!, input.markdown),
  });
}

/**
 * Saved import drafts for this location, newest first. Enabled only when a
 * location is known; the import panel refetches this after an apply or discard.
 */
export function useAdConfigDrafts(practiceId: string | undefined, locationId: string | undefined) {
  return useQuery({
    queryKey: adConfigKeys.drafts(practiceId ?? '', locationId ?? ''),
    queryFn: () => adConfigApi.listDrafts(practiceId!, locationId!),
    enabled: Boolean(practiceId && locationId),
    staleTime: 30_000,
  });
}

/**
 * Apply a confirmed file to a PracticeNova draft. Like diff it reads live, so
 * it is a mutation — it must only run when the operator asks. On success the
 * caller invalidates the drafts list so the new draft appears.
 */
export function useApplyAdConfig(practiceId: string | undefined, locationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { markdown: string; filename?: string }) =>
      adConfigApi.apply(practiceId!, locationId!, input.markdown, input.filename),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adConfigKeys.drafts(practiceId ?? '', locationId ?? ''),
      });
    },
  });
}

/**
 * The dry run for one draft: the exact keyword operations it would push. Reads
 * live, so it is only enabled when the caller opens the preview — never on
 * mount for every draft in the list.
 */
export function useAdConfigDraftOperations(
  practiceId: string | undefined,
  locationId: string | undefined,
  draftId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: adConfigKeys.draftOperations(practiceId ?? '', locationId ?? '', draftId ?? ''),
    queryFn: () => adConfigApi.draftOperations(practiceId!, locationId!, draftId!),
    enabled: Boolean(enabled && practiceId && locationId && draftId),
    staleTime: 15_000,
  });
}

/** Discard a pending draft, then refresh the list. */
export function useDiscardAdConfigDraft(
  practiceId: string | undefined,
  locationId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) => adConfigApi.discardDraft(practiceId!, locationId!, draftId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: adConfigKeys.drafts(practiceId ?? '', locationId ?? ''),
      });
    },
  });
}
